import React, { useState } from 'react';
import { MediaItem } from '../../types';
import { api } from '../../services/api';

interface MediaManagerProps {
  media: MediaItem[];
  onUpdate: () => void;
}

const MediaManager: React.FC<MediaManagerProps> = ({ media, onUpdate }) => {
  const [uploading, setUploading] = useState(false);
  const [showAddUrl, setShowAddUrl] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const [urlTitle, setUrlTitle] = useState('');

  const handleAddUrl = async () => {
    if (!urlInput || !urlTitle) {
      alert('Please provide both URL and title');
      return;
    }

    // Validate and determine type
    let type: MediaItem['type'] = 'video';
    if (urlInput.includes('soundcloud.com')) {
      type = 'soundcloud';
    } else if (urlInput.includes('youtube.com') || urlInput.includes('youtu.be')) {
      type = 'video';
    } else {
      alert('Please enter a valid SoundCloud or YouTube URL');
      return;
    }

    try {
      // For now, just add to mock data
      const newMedia: MediaItem = {
        id: Date.now().toString(),
        type,
        title: urlTitle,
        url: urlInput,
        order: media.length + 1
      };
      
      // In real app, would save to database
      onUpdate();
      setUrlInput('');
      setUrlTitle('');
      setShowAddUrl(false);
    } catch (error) {
      console.error('Failed to add media URL:', error);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      // Determine media type from file
      let type: MediaItem['type'] = 'photo';
      if (file.type.startsWith('video/')) type = 'video';
      else if (file.type.startsWith('audio/')) type = 'audio';

      await api.uploadMedia(file, type);
      onUpdate();
    } catch (error) {
      console.error('Failed to upload media:', error);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex justify-between items-center">
          <h2 className="text-lg font-medium">Media Library</h2>
          <div className="flex gap-2">
            <button
              onClick={() => setShowAddUrl(true)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
              Add SoundCloud/YouTube
            </button>
            <input
              type="file"
              id="media-upload"
              className="sr-only"
              accept="image/*,video/*,audio/*"
              onChange={handleFileUpload}
              disabled={uploading}
            />
            <label
              htmlFor="media-upload"
              className={`cursor-pointer inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                uploading ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {uploading ? 'Uploading...' : 'Upload Media'}
            </label>
          </div>
        </div>
      </div>

      <div className="p-6">
        {media.length === 0 ? (
          <p className="text-gray-500">No media uploaded yet</p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {media.map(item => (
              <div key={item.id} className="relative group">
                {item.type === 'photo' ? (
                  <img
                    src={item.url}
                    alt={item.title}
                    className="w-full h-40 object-cover rounded-lg"
                  />
                ) : item.type === 'video' ? (
                  <div className="w-full h-40 bg-gray-200 rounded-lg flex items-center justify-center">
                    <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </div>
                ) : item.type === 'soundcloud' ? (
                  <div className="w-full h-40 bg-gradient-to-br from-orange-400 to-orange-500 rounded-lg flex items-center justify-center">
                    <svg className="w-12 h-12 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M1.175 12.225c-.051 0-.094.046-.101.1l-.233 2.154.233 2.105c.007.058.05.098.101.098.05 0 .09-.04.099-.098l.255-2.105-.255-2.154c-.01-.057-.05-.1-.099-.1zm.53-.237c-.051 0-.094.045-.103.098l-.185 2.393.185 2.393c.01.053.052.098.103.098.05 0 .092-.045.1-.098l.21-2.393-.21-2.393c-.008-.053-.05-.098-.1-.098zm1.033.018c-.06 0-.105.045-.113.107l-.165 2.369.165 2.375c.008.06.053.105.113.105.058 0 .102-.045.11-.105l.188-2.375-.188-2.369c-.008-.062-.052-.107-.11-.107zm.765.022c-.065 0-.118.053-.125.12l-.14 2.347.14 2.347c.007.065.06.12.125.12.063 0 .116-.055.12-.12l.165-2.347-.165-2.347c-.004-.067-.057-.12-.12-.12zm.762-.007c-.073 0-.132.06-.135.133l-.12 2.328.12 2.325c.003.073.062.133.135.133.072 0 .13-.06.135-.133l.14-2.325-.14-2.328c-.005-.073-.063-.133-.135-.133zm.762.007c-.08 0-.145.065-.148.145l-.095 2.308.095 2.306c.003.08.068.145.148.145.08 0 .145-.065.15-.145l.113-2.306-.113-2.308c-.005-.08-.07-.145-.15-.145zm.973-.037c-.003-.09-.075-.158-.165-.158-.09 0-.162.068-.165.158l-.09 2.365.09 2.365c.003.09.075.158.165.158.09 0 .162-.068.165-.158l.103-2.365-.103-2.365zM7.68 8.302c-.09 0-.174.078-.18.172l-.082 2.98.082 2.987c.006.094.09.172.18.172.09 0 .175-.078.18-.172l.096-2.987-.096-2.98c-.005-.094-.09-.172-.18-.172zm.985-.048c-.003-.105-.09-.188-.195-.188-.106 0-.192.083-.195.188l-.073 3.027.073 3.024c.003.105.09.188.195.188.105 0 .192-.083.195-.188l.087-3.024-.087-3.027zm.976 0c-.105 0-.2.093-.203.202l-.065 2.825.065 2.825c.003.107.098.202.203.202.107 0 .2-.095.203-.202l.078-2.825-.078-2.825c-.003-.11-.096-.202-.203-.202zm.975.023c-.003-.12-.098-.21-.218-.21-.12 0-.215.09-.218.21l-.057 2.802.057 2.802c.003.12.098.21.218.21.12 0 .215-.09.218-.21l.068-2.802-.068-2.802zm1.952-1.455c-.14 0-.253.113-.255.255l-.053 4.252.053 2.738c.002.142.115.255.255.255.142 0 .255-.113.255-.255l.063-2.738-.063-4.252c0-.142-.113-.255-.255-.255zm-.952 1.455c-.135 0-.24.105-.24.24l-.045 2.782.045 2.78c0 .135.105.24.24.24s.24-.105.24-.24l.053-2.78-.053-2.782c0-.135-.105-.24-.24-.24zM18 12c0-2.21-1.79-4-4-4s-4 1.79-4 4 1.79 4 4 4 4-1.79 4-4z"/>
                    </svg>
                  </div>
                ) : (
                  <div className="w-full h-40 bg-gray-200 rounded-lg flex items-center justify-center">
                    <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                    </svg>
                  </div>
                )}
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-opacity rounded-lg flex items-center justify-center">
                  <button className="opacity-0 group-hover:opacity-100 text-white text-sm">
                    {item.isHero ? 'Hero Image' : 'Set as Hero'}
                  </button>
                </div>
                <p className="mt-2 text-sm text-gray-600 truncate">{item.title}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add URL Modal */}
      {showAddUrl && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-medium mb-4">Add SoundCloud or YouTube</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  URL
                </label>
                <input
                  type="url"
                  value={urlInput}
                  onChange={(e) => setUrlInput(e.target.value)}
                  placeholder="https://soundcloud.com/... or https://youtube.com/..."
                  className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title
                </label>
                <input
                  type="text"
                  value={urlTitle}
                  onChange={(e) => setUrlTitle(e.target.value)}
                  placeholder="Track or video title"
                  className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowAddUrl(false);
                  setUrlInput('');
                  setUrlTitle('');
                }}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAddUrl}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Add Media
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MediaManager;